/**
 * Dashboard Page - Claude.ai-inspired Design
 * Main dashboard with today's overview
 * Wired to useDashboard hook with 60s auto-refetch (T050, T055, T056)
 */

import { useDashboard } from '../hooks/useDashboard';
import TodayAppointments from '../components/Dashboard/TodayAppointments';
import OutstandingInvoices from '../components/Dashboard/OutstandingInvoices';
import FinancialSummary from '../components/Dashboard/FinancialSummary';
import RecentActivity from '../components/Dashboard/RecentActivity';
import { DashboardSkeleton } from '../components/ui/SkeletonLoaders';
import { Calendar, DollarSign, FileText, AlertCircle, WifiOff } from 'lucide-react';

export default function DashboardPage() {
  const { data: dashboardData, isLoading, error, isRefetching, failureCount } = useDashboard();

  // Offline indicator (T056)
  const isOffline = failureCount > 2;

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (error && !dashboardData) {
    return (
      <div className="claude-card border-destructive/50 bg-destructive/5 p-6">
        <div className="flex items-center gap-3 text-destructive">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Failed to load dashboard</p>
            <p className="text-sm mt-1 opacity-90">Please try refreshing the page</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-8">
      {/* Offline Indicator (T056) */}
      {isOffline && (
        <div className="claude-card border-warning/50 bg-warning/5 p-4">
          <div className="flex items-center gap-3 text-warning">
            <WifiOff className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-sm">Connection issues detected</p>
              <p className="text-xs mt-0.5 opacity-90">
                Dashboard may be showing stale data. Retrying automatically...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header - Claude-style clean header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1>Dashboard</h1>
          <p className="text-muted-foreground text-lg">
            Welcome back! Here's what's happening today.
          </p>
        </div>
        {isRefetching && (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <div className="w-3 h-3 rounded-full bg-primary/50 animate-pulse" />
            Refreshing...
          </div>
        )}
      </div>

      {/* Metric Cards - Claude-inspired with subtle backgrounds */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Today's Appointments */}
        <div className="claude-card p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div className="space-y-3 flex-1">
              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Today's Appointments
              </p>
              <p className="text-4xl font-semibold tracking-tight">
                {dashboardData?.today.appointments.total || 0}
              </p>
              <p className="text-sm text-muted-foreground">
                {dashboardData?.today.appointments.upcoming || 0} upcoming
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-6 h-6 text-primary" />
            </div>
          </div>
        </div>

        {/* Pending Invoices */}
        <div className="claude-card p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div className="space-y-3 flex-1">
              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Pending Invoices
              </p>
              <p className="text-4xl font-semibold tracking-tight">
                {dashboardData?.invoices.pending.count || 0}
              </p>
              <p className="text-sm text-muted-foreground">
                {dashboardData?.invoices.pending.totalFormatted || '$0.00'}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6 text-success" />
            </div>
          </div>
        </div>

        {/* This Week Revenue */}
        <div className="claude-card p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div className="space-y-3 flex-1">
              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                This Week Revenue
              </p>
              <p className="text-4xl font-semibold tracking-tight">
                {dashboardData?.metrics.thisWeek.revenueFormatted || '$0.00'}
              </p>
              <p className="text-sm text-muted-foreground">
                {dashboardData?.metrics.thisWeek.appointments || 0} appointments
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-6 h-6 text-primary" />
            </div>
          </div>
        </div>

        {/* Overdue Invoices */}
        <div className="claude-card p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div className="space-y-3 flex-1">
              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Overdue Invoices
              </p>
              <p className="text-4xl font-semibold tracking-tight text-destructive">
                {dashboardData?.invoices.overdue.count || 0}
              </p>
              <p className="text-sm text-muted-foreground">
                {dashboardData?.invoices.overdue.totalFormatted || '$0.00'}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-destructive" />
            </div>
          </div>
        </div>
      </div>

      {/* Today's Appointments Widget */}
      {dashboardData && <TodayAppointments data={dashboardData.today} />}

      {/* Outstanding Invoices Widget */}
      {dashboardData && <OutstandingInvoices data={dashboardData.invoices} />}

      {/* Financial Summary Widget */}
      {dashboardData && <FinancialSummary metrics={dashboardData.metrics} />}

      {/* Recent Activity Widget */}
      {dashboardData && <RecentActivity activity={dashboardData.recentActivity} />}
    </div>
  );
}
